import type { QnaQuestion } from "@/lib/api/fddQa";

// Full demo register restored from mockQuestionItems before the old Q&A page was removed.
export type DemoQnaOutcome = "follow-up" | "deal-implication" | "request-info" | "request-analysis" | "clear" | null;
export interface DemoQnaQuestion extends QnaQuestion {
  soWhat?: DemoQnaOutcome;
}

export const DEMO_QNA_QUESTIONS: DemoQnaQuestion[] = [
  {
    id: "demo-0",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Reported_PL_Monthly",
    metric: "Revenue from Main Operations",
    period: "Feb-23",
    value: 0,
    variance: -8,
    question:
      "There was a decline in the account 'Revenue from main operations' (GL: 321) in Feb-23. Please confirm whether this is due to the shorter month, as Feb-23 had 28 days, compared to Feb-24, which was a leap year.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-1",
    soWhat: "deal-implication",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "Reported_PL_Monthly",
    metric: "Other Income",
    period: "Aug-24",
    value: 1250000,
    variance: 485,
    question:
      "Please explain the unusual increase in Other Income (GL:943) of $1.3M in Aug-24 of. Is this recurring or a one-time event?",
    answer:
      "This was a one-off government subsidy received under the Regional Business Growth Fund. It was a one-time grant and is not expected to recur.",
    reviewed: true,
    follow_up:
      "Thank you for confirming. Please clarify: (1) Was the $1.3M received in cash in Aug-24 or accrued earlier? (2) Were there any conditions or clawback provisions attached to the grant? (3) What is the NWC impact — was the receivable recognised before receipt?",
    deal_implication:
      "QoE adjustment: Remove one-off government subsidy of $1.3M from normalised EBITDA. NWC impact — pending Q&A.",
  },
  {
    id: "demo-2",
    soWhat: "follow-up",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Reported_OPEX",
    metric: "Professional Fees",
    period: "Jun-24",
    value: 847500,
    variance: 312,
    question:
      "Please advise the reason for the spike in Professional Fees in Jun-24. Was this one-off in nature?",
    answer:
      "The increase relates to legal and advisory fees incurred in connection with a potential acquisition that was ultimately abandoned. We can provide the invoice breakdown if needed.",
    reviewed: true,
    follow_up:
      "Thank you. Please provide the invoice breakdown so we can confirm the full quantum. Also, were any of these costs capitalised or are they all expensed through P&L?",
    deal_implication: null,
  },
  {
    id: "demo-3",
    soWhat: "clear",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Low",
    databook_tab: "Reported_OPEX",
    metric: "Marketing Spend",
    period: "Sep-24",
    value: 520000,
    variance: 178,
    question:
      "Marketing spend increased significantly in Sep-24. Was this a planned campaign or one-off expenditure?",
    answer:
      "This was a planned product launch campaign for our new premium line. The campaign ran Sep–Oct 2024. We typically run 1–2 major campaigns per year at similar spend levels.",
    reviewed: true,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-4",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Reported_COGS",
    metric: "Cost of Goods Sold",
    period: "Q3-24",
    value: 0,
    variance: 0,
    question:
      "COGS as a percentage of revenue decreased by 3.2pp in Q3-24 compared to Q2-24. Please explain the driver of this margin improvement.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-5",
    soWhat: "request-info",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "Reported_BS_Monthly",
    metric: "Accounts Receivable",
    period: "Oct-24",
    value: 2890000,
    variance: 89,
    question:
      "A/R balance increased 89% in Oct-24. Is this due to timing, a large customer, or a collection issue?",
    answer:
      "The increase is driven by two large invoices raised to Customer A in late Oct-24 totalling $1.8M. Both invoices are within normal 30-day terms.",
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-6",
    soWhat: "deal-implication",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Reported_BS_Monthly",
    metric: "Inventory Valuation",
    period: "Dec-24",
    value: 2340000,
    variance: 45,
    question:
      "What inventory costing method is used (FIFO/LIFO/Weighted Average)? Was any inventory written down in Dec-24?",
    answer:
      "We use weighted average cost. Yes, there was a $340k write-down in Dec-24 for obsolete raw materials that had been in stock for over 18 months. This is an annual exercise performed at year-end.",
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-7",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Reported_FAR",
    metric: "PPE Additions",
    period: "Q3-24",
    value: 1850000,
    variance: 0,
    question:
      "Please provide a breakdown of the $1.85M PPE additions in Q3-24. What assets were acquired and what is their expected useful life?",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-8",
    soWhat: "follow-up",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Reported_Net_Debt",
    metric: "Long-term Debt",
    period: "Nov-24",
    value: 5200000,
    variance: 0,
    question:
      "Please confirm the terms of the $5.2M long-term debt facility, including interest rate, maturity date, and any covenants.",
    answer:
      "The facility is with ANZ Bank at 6.85% fixed, maturing March 2027. There are financial covenants but we would need to check the exact terms with our CFO.",
    reviewed: false,
    follow_up: "Thank you. Please provide the exact covenant terms and confirm whether the $2.2M increase in Nov-24 relates to a new drawdown or facility amendment.",
    deal_implication: null,
  },
  {
    id: "demo-9",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Low",
    databook_tab: "Reported_CF",
    metric: "Operating Cash Flow",
    period: "Q3-24",
    value: 0,
    variance: 0,
    question:
      "Operating cash flow was negative in Q3-24 despite positive EBITDA. Please explain the working capital movements that caused this variance.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-10",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Reported_CF",
    metric: "Capital Expenditure",
    period: "Q3-24",
    value: 0,
    variance: 0,
    question:
      "Please split the Q3-24 capital expenditure between maintenance capex and growth capex.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-11",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "Reported_CF",
    metric: "Dividend Payments",
    period: "FY-24",
    value: 0,
    variance: 0,
    question:
      "Were any dividends or shareholder distributions paid during FY-24? If so, please provide the amounts and dates.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q1",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Revenue",
    metric: "Sales – Monthly Decline",
    period: "Feb-23",
    value: -88000,
    variance: -5,
    question:
      "Revenue from the GL account 'Sales' declined by c.USD 88k in Feb-23 vs. Jan-23. Please confirm whether this is attributable to the shorter calendar month (28 days vs. 31 days), or whether there are operational drivers behind the decline, such as a loss of a customer order or pricing movement.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q2",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "Revenue",
    metric: "Southern Cross Distribution – Customer Concentration",
    period: "FY23–FY24",
    value: 6500000,
    variance: 19,
    question:
      "'Southern Cross Distribution' (GL: Trading Income) was USD 5.5m in FY23 and grew to USD 6.5m in FY24, representing c.19% of total revenue. Southern Cross appears to be the largest single customer. Please confirm the contractual terms with this customer, including notice periods and any volume rebate arrangements.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q3",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Revenue",
    metric: "Recurring Revenue Growth",
    period: "FY23–FY24",
    value: 283000,
    variance: 53,
    question:
      "'Recurring revenue' (GL: Trading Income) grew from USD 185k in FY23 to USD 283k in FY24, a 53% increase. Please explain what this revenue stream represents (subscription, service agreements, etc.) and provide a schedule of the underlying contracts, including start dates, duration and monthly/annual value.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q4",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Revenue",
    metric: "Freight & Distribution Revenue",
    period: "FY23–LTM25",
    value: 6500,
    variance: 167,
    question:
      "'Freight and distribution' revenue (GL: Trading Income) grew from USD 0.9k in FY23 to USD 2.4k in FY24, and USD 6.5k in LTM25. Please confirm whether freight is charged at cost recovery or at a mark-up, and whether this is a deliberate revenue strategy or reflects pass-through billing.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q5",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Revenue",
    metric: "Harbour Logistics NZ – Lost Customer",
    period: "FY23–FY24",
    value: 0,
    variance: -100,
    question:
      "'Harbour Logistics NZ' contributed USD 253k in FY23 revenue but generated nil revenue in FY24 and LTM25. Please explain the reason for this customer's disappearance from the revenue base and confirm whether there is any outstanding dispute, contract termination, or outstanding receivable associated with this customer.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q6",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "Revenue",
    metric: "Gross Margin Decline",
    period: "FY24–LTM25",
    value: 0,
    variance: -29,
    question:
      "The gross margin declined from 46.7% in FY24 to 33.2% in LTM25. Please provide a bridge of gross margin movements by cost category (purchases, stock movements, cups/lids, delivery, etc.) to explain the key drivers.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q7",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Revenue",
    metric: "Loyalty Programs",
    period: "FY23–FY24",
    value: 74000,
    variance: 106,
    question:
      "'Loyalty programs' (GL: Trading Income) recorded USD 36k in FY23 and USD 74k in FY24. Please confirm whether this represents revenue earned from a loyalty scheme or costs associated with running one, and clarify the accounting treatment.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q8",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Cost of Goods Sold",
    metric: "Closing Stock / Inventory Aging",
    period: "Jun-23 to Oct-24",
    value: 1500000,
    variance: 271,
    question:
      "Closing stock increased from USD 404k at Jun-23 to USD 1.1m at Jun-24 and USD 1.5m at Oct-24. The inventory aging schedule shows c.25% of inventory is aged over one year. Please confirm whether any write-down provisions have been recorded and whether aged inventory is still sellable at full value.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q9",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Cost of Goods Sold",
    metric: "Aurora Systems – Largest Supplier",
    period: "FY23–FY24",
    value: 1430000,
    variance: -9,
    question:
      "'Purchases' from 'Aurora Systems Pty Ltd' (GL: Cost of Sales) totalled USD 1.58m in FY23 and USD 1.43m in FY24, making Aurora the single largest supplier. Please provide the supply agreement with Aurora, including pricing terms, payment terms, exclusivity, and whether alternative suppliers exist.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q10",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "Cost of Goods Sold",
    metric: "Personal Expense Reimbursements in COGS",
    period: "FY23–FY24",
    value: 392000,
    variance: 626,
    question:
      "'Personal Expense Reimbursements' (GL: Cost of Sales) increased from USD 54k in FY23 to USD 392k in FY24. This is an unusual item to sit within COGS. Please provide a breakdown of what is included and confirm whether these are genuinely COGS-related or have been misclassified.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q11",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Cost of Goods Sold",
    metric: "Delivery Expenses",
    period: "FY23–FY24",
    value: 120000,
    variance: 7,
    question:
      "'Delivery Expenses' (GL: Cost of Sales) increased from USD 112k in FY23 to USD 120k in FY24 (+7.2%), ahead of revenue growth of 3.7%. Please confirm whether delivery costs are variable or semi-fixed and explain the above-revenue-growth cost increase.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q12",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Cost of Goods Sold",
    metric: "Opening Stock Monthly Movement – Accuracy Flag",
    period: "FY24",
    value: 0,
    variance: 0,
    question:
      "The 'Opening Stock Monthly Movement' GL account is flagged with an Accuracy Flag of 'False' in the Mapping sheet. Please confirm what this account captures and why it has been flagged, and whether the mapping to COGS is intentional or represents a data quality issue.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q13",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Operating Expenses",
    metric: "Rent",
    period: "FY23–LTM25",
    value: 336000,
    variance: 24,
    question:
      "'Rent' (GL: Operating Expenses) increased from USD 300k in FY23 to USD 371k in FY24 (+23.8%), yet dropped to USD 336k in LTM25. Please provide the lease schedule including all premises, rent review dates, lease expiry dates, and any break clauses, and explain the FY24 step-up and subsequent reduction.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q14",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "Operating Expenses",
    metric: "Royalty Fees",
    period: "FY23–LTM25",
    value: 240000,
    variance: 0,
    question:
      "'Royalty fees' (GL: Operating Expenses) are fixed at USD 240k per year across FY23, FY24 and LTM25. Please provide the royalty agreement, including the counterparty, term, renewal options, and whether the royalty rate would survive a change of control.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q15",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Operating Expenses",
    metric: "Repairs & Maintenance",
    period: "FY23–LTM25",
    value: 67000,
    variance: -34,
    question:
      "'Repairs & Maintenance' (GL: Operating Expenses) declined from USD 131k in FY23 to USD 86k in FY24 (-34%) and USD 67k in LTM25. Please confirm whether this reflects genuine efficiencies, a change in policy, or deferred maintenance, and provide the maintenance schedule for key plant and equipment.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q16",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Operating Expenses",
    metric: "Workers' Compensation",
    period: "FY24–LTM25",
    value: 47000,
    variance: 135,
    question:
      '"Workers\' Compensation" (GL: Operating Expenses) more than doubled from USD 20k in FY24 to USD 47k in LTM25. Please explain this increase — specifically whether it reflects a premium rate change, headcount increase, deterioration in claims history, or a one-off adjustment.',
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q17",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Operating Expenses",
    metric: "Superannuation – Director",
    period: "FY23–LTM25",
    value: 57500,
    variance: 109,
    question:
      "'Superannuation – Director' (GL: Operating Expenses) increased from USD 27.5k in FY23/FY24 to USD 57.5k in LTM25. Please confirm whether this reflects a statutory rate change, a new director, or a catch-up contribution, and whether this cost will recur post-acquisition.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q18",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "QoE_Normalisations",
    metric: "Non-business-related Expenses Add-back",
    period: "FY23–FY24",
    value: 240000,
    variance: 0,
    question:
      "Management has added back USD 240k per year in FY23 and FY24 as 'Non-business-related expenses' (Normalisation #3). Please provide a detailed schedule of the underlying costs included in this add-back, with supporting documentation, and confirm the basis for treating these as non-recurring.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q19",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "QoE_Normalisations",
    metric: "Shareholder Salary Normalisation",
    period: "FY23–FY24",
    value: 27500,
    variance: 0,
    question:
      "A normalisation for 'Shareholder salary normalisation to market rates' of USD 27.5k per year has been applied in FY23 and FY24. Please confirm the actual salary paid to the relevant shareholder-director, the benchmark used to determine market rate, and the basis for the USD 27.5k uplift.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q20",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "QoE_Normalisations",
    metric: "Unrealised Currency Gains Removal",
    period: "FY23–FY24",
    value: -9900,
    variance: 0,
    question:
      "A FDD normalisation of USD (9.7k) in FY23 and USD (9.9k) in FY24 has been applied for 'Removal of unrealised currency gains'. Please confirm which foreign currency balances give rise to these gains, whether hedging is in place, and the functional currency of the business.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q21",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "QoE_Super",
    metric: "Bad Debt – Loan (One-off)",
    period: "FY23",
    value: 21200,
    variance: 0,
    question:
      "A one-off FDD normalisation of USD 21.2k was applied in FY23 for 'Bad Debt – Loan'. Please confirm the identity of the counterparty, the original loan amount, the date of write-off, and whether any recovery action is ongoing or the counterparty is a related party.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q22",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "Net Working Capital",
    metric: "Trade Debtors – Aged >90 Days",
    period: "Jun-23 to Jun-24",
    value: 403000,
    variance: 30,
    question:
      "Trade debtors increased from USD 310k at Jun-23 to USD 403k at Jun-24 (+30%), with 'Three Months+' aged debtors growing from USD 11.3k to USD 32.7k. Please provide a customer-by-customer breakdown of balances aged over 90 days and confirm whether any provisions for doubtful debts are held.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q23",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Net Working Capital",
    metric: "Payment Terms & Collections",
    period: "FY24",
    value: 0,
    variance: 0,
    question:
      "Please confirm the standard payment terms offered to customers, whether payment terms vary by customer, and the collection process for overdue accounts.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q24",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Net Working Capital",
    metric: "Trade Creditors Decline",
    period: "Jun-23 to Oct-24",
    value: 53000,
    variance: -73,
    question:
      "Trade creditors decreased from USD 193k at Jun-23 to USD 128k at Jun-24 (-33%) and further to USD 53k at Oct-24. Please confirm whether the business has paid down creditors ahead of the transaction or whether this reflects a genuine reduction in purchasing activity, and confirm standard supplier payment terms.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q25",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Net Working Capital",
    metric: "Prepayments Spike",
    period: "Jun-24 to Oct-24",
    value: 169000,
    variance: 604,
    question:
      "Prepayments fell from USD 47k at Jun-23 to USD 24k at Jun-24, then spiked to USD 169k at Oct-24. Please provide a breakdown of the prepayments balance at each balance sheet date and explain the Oct-24 increase.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q26",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "Net Debt & Balance Sheet",
    metric: "Shareholder Loans",
    period: "Jun-23 to Oct-24",
    value: 422900,
    variance: 0,
    question:
      "Shareholder loans moved from USD (1.07m) at Jun-23 to USD 12.3k at Jun-24 and USD 422.9k at Oct-24. Please confirm the terms of the shareholder loan, whether it is being repaid or novated prior to completion, and the tax treatment of any interest charged.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q27",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Net Debt & Balance Sheet",
    metric: "Cash Decline vs Positive EBITDA",
    period: "Jul-23 to Oct-24",
    value: 3750000,
    variance: -22,
    question:
      "Cash declined from USD 4.79m at Jun-23 to USD 3.75m at Oct-24 despite positive EBITDA. Please provide a cash flow bridge for the period Jul-23 to Oct-24 explaining the reduction, including capex, working capital movements and debt service.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q28",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Net Debt & Balance Sheet",
    metric: "Income Tax Balance Movements",
    period: "Jun-23 to Oct-24",
    value: 55000,
    variance: 0,
    question:
      "The income tax balance moved from a receivable of USD 115k at Jun-23 to a payable of USD (35k) at Jun-24 and a receivable of USD 55k at Oct-24. Please confirm whether all tax returns are up to date and whether any IRD audits or disputes are outstanding.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q29",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Net Debt & Balance Sheet",
    metric: "Land & Building – Fixed Assets",
    period: "Oct-24",
    value: 1220000,
    variance: 0,
    question:
      "The fixed asset schedule shows 'Land and Building' with a net book value of USD 1.22m at Oct-24, after accumulated depreciation of USD 6.6m against an original cost of USD 7.9m. Please confirm whether the property is owned or leased, when it was last independently valued, and whether it is included in the transaction perimeter.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q30",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Critical",
    databook_tab: "Capital Expenditure",
    metric: "Growth vs Maintenance Capex Split",
    period: "FY23–FY24",
    value: 1080000,
    variance: -12,
    question:
      "Total capex was USD 1.23m in FY23 and USD 1.08m in FY24, with maintenance capex representing c.35–41% of total spend. Please confirm the basis for the growth vs. maintenance classification and whether the maintenance capex level is sufficient to maintain current revenue capacity.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q31",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "High",
    databook_tab: "Capital Expenditure",
    metric: "YTD25 Capex Run-Rate",
    period: "YTD25",
    value: 1170000,
    variance: 0,
    question:
      "YTD25 capex of USD 1.17m is already approaching full-year FY23/FY24 levels. Please confirm the full-year FY25 capex budget, the key projects driving elevated spend, and whether any committed capex will be required post-completion.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q32",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Employees & Wages",
    metric: "Wages & Salaries Trend",
    period: "FY24–LTM25",
    value: 921000,
    variance: 7,
    question:
      "Wages & salaries are tracking to an annualised USD 921k in LTM25 vs. USD 858k in FY24. Please provide a headcount schedule by role/department showing FTE count at Jun-23, Jun-24 and Oct-24, together with annualised salary cost per FTE category.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-q33",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Employees & Wages",
    metric: "Superannuation Expense Growth",
    period: "FY23–LTM25",
    value: 100000,
    variance: 20,
    question:
      "Superannuation expense grew from USD 83k in FY23 to USD 90k in FY24 and USD 100k in LTM25, growing faster than implied headcount and wages. Please confirm the employer contribution rate and whether any catch-up contributions or accrual adjustments were made in FY24 or LTM25.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-ap-1",
    soWhat: "request-analysis",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Accounting Policies – Revenue Recognition",
    metric: "Revenue Recognition",
    period: "FY23-FY24",
    value: 0,
    variance: 0,
    question:
      "Please confirm the revenue recognition policy applied, including the point at which revenue is recognised (e.g., on delivery, on invoice, on completion of service). Are there any contracts with multiple performance obligations, and if so, how is revenue allocated across them?",
    answer:
      "Revenue is recognised on a cash basis when payment is received. We do not accrue for revenue. There are no contracts with multiple performance obligations.",
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-ap-2",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Accounting Policies – Inventory Valuation",
    metric: "Inventory Valuation",
    period: "FY23-FY24",
    value: 0,
    variance: 0,
    question:
      "How does management identify and provide for slow-moving, damaged, or obsolete stock? Is there a seasonal pattern to inventory holdings? If so, please describe the typical cycle and identify which months represent peak and trough inventory levels.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-ap-3",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Accounting Policies – Inventory Valuation",
    metric: "Stock Take Process",
    period: "FY23-FY24",
    value: 0,
    variance: 0,
    question:
      "Please describe the stock take process: (a) was it a full physical count or cycle count, (b) who performed the count, (c) what reconciliation was performed to the accounting records, and (d) what was the quantum of any count adjustments?",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-ap-4",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Accounting Policies – Stock in Transit Recognition",
    metric: "Stock in Transit",
    period: "FY23-FY24",
    value: 0,
    variance: 0,
    question:
      "Please confirm the accounting policy for recognising Stock in Transit (SIT). At what point does legal title and risk transfer? Is recognition based on bill of lading date, shipment date, or arrival at port/warehouse?",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
  {
    id: "demo-ap-5",
    revision: 1,
    is_custom: false,
    status: "Open",
    priority: "Medium",
    databook_tab: "Accounting Policies – Depreciation",
    metric: "Depreciation Policy",
    period: "FY23-FY24",
    value: 0,
    variance: 0,
    question:
      "Please confirm the depreciation policy for each class of fixed asset, including useful lives applied, depreciation method (straight-line, diminishing value, or units of production), and whether any assets have been revalued during the Historical Period.",
    answer: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  },
];
